/**
 * MODAL: CREATE DISCOUNT
 * Modal pour créer une nouvelle réduction
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { CreateDiscountInput } from '../../schemas/discount';
import { createDiscountSchema } from '../../schemas/discount';
import { DISCOUNT_STATUSES } from '../../types/discount';
import { Form, FormInput, FormSelect } from '../ui';
import { Modal, ModalFooter } from '../ui/modal';

interface CreateDiscountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateDiscountInput) => Promise<void>;
}

export function CreateDiscountModal({ isOpen, onClose, onSubmit }: CreateDiscountModalProps) {
  const form = useForm<CreateDiscountInput>({
    resolver: zodResolver(createDiscountSchema),
    defaultValues: {
      status: 'Active',
    },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleFormSubmit = async (data: CreateDiscountInput) => {
    await onSubmit(data);
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Créer une réduction"
      size="md"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel="Créer"
          isSubmitting={form.formState.isSubmitting}
          formId="create-discount-form"
        />
      }
    >
      <Form {...form}>
        <form id="create-discount-form" onSubmit={form.handleSubmit(handleFormSubmit)}>
          <div className="flex flex-col gap-4">
            <FormInput
              control={form.control}
              name="name"
              label="Nom de la réduction"
              required
              placeholder="Ex: Early Birds"
            />

            <FormInput
              control={form.control}
              name="amount"
              label="Montant (EUR)"
              type="number"
              required
              placeholder="Ex: 100"
              valueAsNumber
            />

            <FormSelect
              control={form.control}
              name="status"
              label="Statut"
              required
              options={DISCOUNT_STATUSES.map((status) => ({
                value: status,
                label: status,
              }))}
            />
          </div>
        </form>
      </Form>
    </Modal>
  );
}
